/**
 * Iter 67ch — Public blog list page (per category).
 *
 * Routes:
 *   /blog/destinations  → category="destinations"
 *   /blog/travel-tips   → category="travel-tips"
 *
 * Renders a grid of post cards filtered to the category and (optionally) a
 * country. Empty-state shows a "Coming soon" placeholder rather than blanking.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Globe2, BookOpen, ArrowRight, Loader2 } from "lucide-react";

const CATEGORY_META = {
  "destinations": { label: "Destinations", heading: "Popular destinations", subheading: "Country-by-country guides curated by RentalCar.al hosts." },
  "travel-tips":  { label: "Travel tips",  heading: "Travel tips for renters", subheading: "Driving rules, parking, fuel, and border crossings — local know-how." },
};

function _resolveSrc(path) {
  if (!path) return null;
  return /^https?:\/\//i.test(path) ? path : fileUrl(path);
}

export default function BlogListPage() {
  const { category } = useParams();
  const [search, setSearch] = useSearchParams();
  const countryFilter = search.get("country") || "";
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const meta = CATEGORY_META[category] || { label: "Blog", heading: "Blog", subheading: "" };

  // Update document title for SEO without pulling in react-helmet-async.
  useEffect(() => {
    document.title = `${meta.heading} · RentalCar.al`;
  }, [meta.heading]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = { category, limit: 24 };
    if (countryFilter) params.country = countryFilter;
    api.get("/public/blog", { params })
      .then((r) => { if (alive) setPosts(r.data || []); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [category, countryFilter]);

  const countries = useMemo(() => Array.from(new Set(posts.map((p) => p.country).filter(Boolean))).sort(), [posts]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-3 flex items-center gap-1.5">
            <BookOpen className="h-3 w-3" /> {meta.label}
          </div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight font-medium">{meta.heading}</h1>
          <p className="mt-3 text-neutral-600 text-sm max-w-2xl">{meta.subheading}</p>
        </div>

        {countries.length >= 2 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-country-chips">
            <button
              type="button"
              onClick={() => setSearch({})}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition ${!countryFilter ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"}`}
            >
              All
            </button>
            {countries.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setSearch({ country: c })}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition ${countryFilter === c ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"}`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div className="py-20 text-center text-neutral-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…</div>
        )}

        {!loading && posts.length === 0 && (
          <div className="rounded-3xl border border-dashed border-border bg-neutral-50/60 p-16 text-center">
            <div className="font-display text-2xl mb-2">Coming soon</div>
            <p className="text-sm text-neutral-500 max-w-md mx-auto">We&apos;re curating the first wave of {meta.label.toLowerCase()}. Check back in a few days.</p>
          </div>
        )}

        {!loading && posts.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${category}/${p.slug}`}
                className="group rounded-3xl overflow-hidden bg-white border border-border hover:shadow-lg transition-all"
                data-testid={`blog-card-${p.slug}`}
              >
                <div className="aspect-[16/10] overflow-hidden bg-neutral-100">
                  {p.featured_image
                    ? <img src={_resolveSrc(p.featured_image)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    : <div className="w-full h-full flex items-center justify-center text-neutral-300"><BookOpen className="h-8 w-8" /></div>}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {p.country && (
                      <Badge variant="outline" className="text-[10px] uppercase tracking-[0.15em] font-bold">
                        <Globe2 className="h-3 w-3 mr-0.5" /> {p.country}
                      </Badge>
                    )}
                    {p.is_featured && <Badge className="bg-amber-100 text-amber-800 text-[10px]">Featured</Badge>}
                  </div>
                  <h2 className="font-display text-lg font-semibold text-neutral-900 group-hover:text-blue-700 transition">{p.title}</h2>
                  {p.excerpt && <p className="text-sm text-neutral-500 mt-2 line-clamp-2">{p.excerpt}</p>}
                  <div className="mt-3 inline-flex items-center text-xs font-semibold text-blue-600">
                    Read more <ArrowRight className="h-3 w-3 ml-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
